import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import intlTelInput from "intl-tel-input";
import "intl-tel-input/build/css/intlTelInput.css";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { toLocationValue } from "../config/locationLabels";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { trackEvent } from "../hooks/useAnalytics";
import { cache, CACHE_KEYS } from "../utils/cacheManager";
import AvatarUpload from "./AvatarUpload";
import "./GeneralForm.css";
import InputLogin from "./InputLogin";
import LocationAutocomplete from "./LocationAutocomplete";
import TextArea from "./TextArea";

const FormEditProfile = ({ setLoading }) => {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const { showAlert } = useAlert();

    const [fName, setFName] = useState("");
    const [lName, setLName] = useState("");
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [bio, setBio] = useState("");
    const [category, setCategory] = useState([]);
    const [location, setLocation] = useState(null);
    const [driverLicense, setDriverLicense] = useState("");
    const [isOpenLicense, setIsOpenLicense] = useState(false);
    const selectRefLicense = useRef(null);

    const LICENSE_OPTIONS = [
        { value: "", label: "Nemám" },
        { value: "B", label: "B" },
        { value: "C", label: "C" },
        { value: "D", label: "D" },
        { value: "E", label: "E" },
    ];
    const [email, setEmail] = useState("");

    // Uložené číslo z databáze
    const [savedPhone, setSavedPhone] = useState("");
    const [currentPhone, setCurrentPhone] = useState("");
    const [phoneVisible, setPhoneVisible] = useState(false);
    const [phoneChanged, setPhoneChanged] = useState(false);
    const [showPhonePopup, setShowPhonePopup] = useState(false);
    const [pendingPhone, setPendingPhone] = useState("");

    const phoneFormRef = useRef(null);
    const phoneFormInstance = useRef(null);
    const phonePopupRef = useRef(null);
    const phonePopupInstance = useRef(null);

    const [phonePopupStep, setPhonePopupStep] = useState("phone");
    const [otp, setOtp] = useState("");
    const [countdown, setCountdown] = useState(0);
    const [alreadyTried, setAlreadyTried] = useState(false);

    // Načtení dat uživatele
    useEffect(() => {
        if (!user) return;

        setFName(user.first_name || "");
        setLName(user.last_name || "");
        setBio(user.bio || "");
        setLocation(toLocationValue(user));
        setDriverLicense(user.driver_license || "");
        setEmail(user.email || "");

        // Uložení původního telefonního čísla
        const userPhone = user.phone || "";
        setSavedPhone(userPhone);
        setCurrentPhone(userPhone);
        setPhoneVisible(user.phone_visible || false);

        // Načtení avataru
        if (user.profile_image_url) {
            setAvatarPreview(user.profile_image_url);
        }
    }, [user]);

    // Synchronizace checkboxů specializací podle načtených dat
    // i po reloadu, jakmile se načte user z API/Contextu
    useEffect(() => {
        if (user && user.specs) {
            const userSlugs = user.specs.map((s) => s.slug);
            // Porovnáme, jestli se data skutečně liší, abychom se vyhnuli nekonečnému loopu
            setCategory(userSlugs);
        }
    }, [user?.specs]); // Sledujeme přímo vnořené pole specs

    // Inicializace hlavního phone inputu
    useEffect(() => {
        if (!phoneFormRef.current) return;

        phoneFormInstance.current = intlTelInput(phoneFormRef.current, {
            initialCountry: "cz",
            preferredCountries: ["cz", "sk"],
            separateDialCode: true,
            utilsScript: null,
        });

        // Propagace uloženého čísla do vstupního pole
        if (savedPhone) {
            phoneFormInstance.current.setNumber(savedPhone);
        }

        return () => {
            phoneFormInstance.current?.destroy();
            phoneFormInstance.current = null;
        };
    }, [savedPhone]);

    // Inicializace popup phone inputu
    useEffect(() => {
        if (!showPhonePopup || !phonePopupRef.current) return;

        phonePopupInstance.current = intlTelInput(phonePopupRef.current, {
            initialCountry: "cz",
            preferredCountries: ["cz", "sk"],
            separateDialCode: true,
            utilsScript: null,
        });

        // Nastav aktuální číslo do popup inputu
        if (currentPhone) {
            phonePopupInstance.current.setNumber(currentPhone);
        }

        return () => {
            phonePopupInstance.current?.destroy();
            phonePopupInstance.current = null;
        };
    }, [showPhonePopup, currentPhone]);

    // ESC zavře popup
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape" && showPhonePopup) {
                handleClosePopup();
            }
        };
        document.addEventListener("keydown", handleEsc);
        return () => document.removeEventListener("keydown", handleEsc);
    }, [showPhonePopup]);

    // Countdown
    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isOpenLicense && !selectRefLicense.current?.contains(event.target))
                setIsOpenLicense(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpenLicense]);

    // Kontrola změny telefonního čísla při události onBlur
    const handlePhoneBlur = () => {
        const iti = phoneFormInstance.current;
        const input = phoneFormRef.current;

        if (!iti || !input) return;

        const raw = input.value.trim();

        // Pokud je pole prázdné
        if (!raw) {
            setCurrentPhone("");
            // Pokud předtím v savedPhone něco bylo, označíme to jako změnu
            setPhoneChanged(savedPhone !== "");
            return;
        }

        const digits = raw.replace(/\D/g, "");
        const dialCode = iti.getSelectedCountryData().dialCode;
        const fullNumber = `+${dialCode}${digits}`;

        setCurrentPhone(fullNumber);
        setPhoneChanged(fullNumber !== savedPhone);
    };

    const handleAvatarReady = (file) => {
        if (avatarPreview?.startsWith("blob:")) {
            URL.revokeObjectURL(avatarPreview);
        }
        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const toggleCategory = (value) => {
        setCategory((prev) =>
            prev.includes(value)
                ? prev.filter((v) => v !== value)
                : [...prev, value],
        );
    };

    // Popup krok 1: odeslání OTP kódu
    const handlePopupSubmit = async () => {
        const iti = phonePopupInstance.current;
        const input = phonePopupRef.current;
        setLoading(true);

        // Pokud už máme uložené číslo (opakované odeslání z OTP kroku)
        if (pendingPhone && phonePopupStep === "otp") {
            try {
                await sendOtp(pendingPhone);
                setLoading(false);
            } catch (err) {
                setLoading(false);
                showAlert(
                    "error",
                    err.response?.data?.error || "Chyba při odesílání SMS",
                );
            }
            return;
        }

        if (!iti || !input) {
            setLoading(false);
            showAlert("error", "Telefonní pole je prázdné");
            return;
        }

        const raw = input.value.trim();
        const digits = raw.replace(/\D/g, "");

        if (!digits) {
            setLoading(false);
            showAlert("error", "Zadejte telefonní číslo");
            return;
        }

        const dialCode = iti.getSelectedCountryData().dialCode;
        const phoneNumber = `+${dialCode}${digits}`;

        try {
            // 1️ Lookup (normalizace čísla)
            const lookup = await axiosInstance.post("/api/phone-lookup", {
                phone: phoneNumber,
            });

            const normalizedPhone = lookup.data.phone;

            // 2️ Kontrola duplicity (jiný uživatel)
            const check = await axiosInstance.post("/api/phone-check", {
                phone: normalizedPhone,
            });

            if (check.data.exists) {
                showAlert("error", "Číslo je již přiřazené jinému uživateli");
                setLoading(false);
                return;
            }

            // 3️ Uložení a odeslání OTP
            setPendingPhone(normalizedPhone);
            setCurrentPhone(normalizedPhone);
            await sendOtp(normalizedPhone);
            setLoading(false);
        } catch (err) {
            setLoading(false);
            showAlert(
                "error",
                err.response?.data?.error || "Chyba při odesílání SMS",
            );
        }
    };

    const sendOtp = async (phoneNumber) => {
        try {
            await axiosInstance.post("/api/send-otp", {
                phone: phoneNumber,
            });

            setPhonePopupStep("otp");
            setCountdown(59);
            setAlreadyTried(true);
            setLoading(false);
            showAlert("success", "Ověřovací SMS byla odeslána");
        } catch (err) {
            setLoading(false);
            showAlert(
                "error",
                err.response?.data?.error || "Chyba při odesílání SMS",
            );
        }
    };

    // Popup krok 2: ověření OTP kódu
    const handleVerifyOtp = async () => {
        setLoading(true);
        if (otp.length !== 6) {
            setLoading(false);
            showAlert("error", "Kód musí mít 6 číslic");
            return;
        }

        try {
            const res = await axiosInstance.post("/api/verify-otp", {
                phone: currentPhone,
                code: otp,
            });

            if (!res.data.success) {
                setLoading(false);
                showAlert("error", "Neplatný kód");
                return;
            }
            setLoading(false);
            showAlert("success", "Telefon byl ověřen");

            // Aktualizace uloženého čísla a uzavření popupu
            setSavedPhone(currentPhone);
            setPhoneChanged(false);
            handleClosePopup();
        } catch (err) {
            setLoading(false);
            // Pokud API vrátilo 422 s konkrétní chybou, použijeme ji
            const message = err.response?.data?.message || err.response?.data?.error;
            if (message && (message.includes("kód") || message.includes("code"))) {
                showAlert("error", "Neplatný kód");
            } else {
                showAlert("error", "Chyba při ověřování kódu");
            }
        }
    };

    const handleClosePopup = () => {
        setShowPhonePopup(false);
        setPhonePopupStep("phone");
        setOtp("");
        setCountdown(0);
    };

    // Odeslání formuláře profilu
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (phoneChanged && currentPhone !== "") {
            showAlert(
                "error",
                "Telefonní číslo bylo změněno a musí být ověřeno",
            );
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append("_method", "PUT");
            formData.append("bio", bio);
            formData.append("location", location?.label || "");
            formData.append("city", location?.city || location?.label || "");
            formData.append("postcode", location?.postcode || "");
            formData.append("latitude", location?.latitude ?? "");
            formData.append("longitude", location?.longitude ?? "");

            formData.append("phone", currentPhone || "");

            formData.append("driver_license", driverLicense);

            formData.append("phone_visible", phoneVisible ? "1" : "0");

            category.forEach((cat) => {
                formData.append("spec[]", cat);
            });

            if (avatarFile) {
                formData.append("avatar", avatarFile);
            }

            for (let [key, value] of formData.entries()) {
                console.log(`  ${key}:`, value);
            }

            if (bio.length > 800) {
                setLoading(false);
                showAlert("error", "Bio je příliš dlouhé. Zkraťte jej prosím.");
                return; // Zastaví odesílání
            }

            const response = await axiosInstance.post("/api/user", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // Aktualizace dat
            setUser(response.data.user);
            // Profil v localStorage musí odpovídat uloženému, jinak se při
            // dalším otevření editace načtou staré hodnoty
            cache.setUserData(response.data.user);
            cache.remove(CACHE_KEYS.PROFILE_ELIGIBLE);

            // Zpracování zprávy alertu - Vezmeme zprávu z backendu
            const isDeactivated = response.data.deactivated;
            const msg =
                response.data.message || "Profil byl úspěšně aktualizován.";

            // Zobrazení alertu - pokud došlo k vypnutí módu, dáme "warning" nebo "info"
            showAlert(isDeactivated ? "warning" : "success", msg);

            trackEvent("profile_edit", {
                user_id: response.data.user?.id,
            });

            // Navigace - Pokud chceš, aby uživatel viděl ten alert,
            // můžeš navigaci o vteřinu odložit, nebo se ujisti, že AlertContext přežije navigaci
            setTimeout(() => {
                navigate(`/user/${response.data.user.id}`);
            }, 100);
        } catch (err) {
            setLoading(false);
            const messages = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat()
                : [err.response?.data?.message || "Došlo k chybě"];
            messages.forEach((msg) => showAlert("error", msg));
        }
    };

    return (
        <>
            {/* POPUP PRO OVĚŘENÍ TELEFONU */}
            {showPhonePopup && (
                <div className="popup_container" onClick={handleClosePopup}>
                    <div className="popup" onClick={(e) => e.stopPropagation()}>
                        <h2>Ověření telefonního čísla</h2>

                        <div className="cropped">
                            {/* PHONE STEP */}
                            {phonePopupStep === "phone" && (
                                <div>
                                    <input
                                        type="tel"
                                        id="phone2"
                                        className="input-login"
                                        placeholder="Zadejte číslo"
                                        ref={phonePopupRef}
                                    />
                                    <button
                                        type="button"
                                        className="form-submit"
                                        onClick={handlePopupSubmit}
                                    >
                                        <p className="strong">
                                            Odeslat ověřovací SMS
                                        </p>
                                    </button>
                                </div>
                            )}

                            {/* OTP STEP */}
                            {phonePopupStep === "otp" && (
                                <div>
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={6}
                                        className="input-login"
                                        placeholder="Zadejte 6místný kód"
                                        value={otp || ""}
                                        onChange={(e) =>
                                            setOtp(
                                                e.target.value.replace(
                                                    /\D/g,
                                                    "",
                                                ),
                                            )
                                        }
                                    />
                                    <button
                                        type="button"
                                        className="form-submit"
                                        onClick={handleVerifyOtp}
                                    >
                                        <p className="strong">Potvrdit</p>
                                    </button>
                                    <button
                                        type="button"
                                        className="cropped-send_again"
                                        onClick={handlePopupSubmit}
                                        disabled={countdown > 0}
                                    >
                                        {countdown > 0 ? (
                                            <p className="strong button_disabled">
                                                Odeslat znovu za {countdown}s
                                            </p>
                                        ) : (
                                            <p className="strong">
                                                Odeslat znovu
                                            </p>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* HLAVNÍ FORMUL */}
            <form className="form_edit_profile" onSubmit={handleSubmit}>
                {/* Jméno */}
                <div className="name">
                    <label
                        htmlFor="first_name"
                        className="body_base label-move"
                    >
                        Křestní jméno
                    </label>
                    <InputLogin
                        type="text"
                        name="first_name"
                        value={fName || ""}
                        required
                        extraClass="disabled-input"
                        disabled
                    />
                    <div className="gap_between_names"></div>
                    <label htmlFor="last_name" className="body_base label-move">
                        Příjmení
                    </label>
                    <InputLogin
                        type="text"
                        name="last_name"
                        value={lName || ""}
                        required
                        extraClass="disabled-input"
                        disabled
                    />
                </div>

                {/* Avatar */}
                <div className="avatar">
                    <AvatarUpload
                        avatarPreview={avatarPreview}
                        onAvatarReady={handleAvatarReady}
                    />
                </div>

                {/* Bio */}
                <div className="bio">
                    <label htmlFor="bio" className="body_base label-move">
                        Bio
                    </label>
                    <TextArea
                        name="bio"
                        placeholder="např. Pracoval jsem 2 roky jako bedák..."
                        value={bio || ""}
                        rows="7"
                        maxLength="800"
                        onChange={(e) => setBio(e.target.value)}
                    />
                </div>

                {/* Specializace */}
                <div className="category">
                    <legend>Specializace</legend>
                    <div className="category-grid">
                        {[
                            "light_technician",
                            "sound_technician",
                            "av_technician",
                            "rigger",
                            "stagehands",
                        ].map((spec) => (
                            <label key={spec} className="checkbox">
                                <input
                                    type="checkbox"
                                    value={spec}
                                    checked={category.includes(spec)}
                                    onChange={() => toggleCategory(spec)}
                                />
                                <span>
                                    {spec === "light_technician" &&
                                        "Osvětlovač"}
                                    {spec === "sound_technician" && "Zvukař"}
                                    {spec === "av_technician" && "AV technik"}
                                    {spec === "rigger" && "Rigger"}
                                    {spec === "stagehands" && "Stagehands"}
                                    <FontAwesomeIcon
                                        icon={faCircleCheck}
                                        className="input-selected-icon"
                                    />
                                </span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Lokalita */}
                <div className="location">
                    <label htmlFor="location" className="body_base label-move">
                        Lokalita
                    </label>
                    <LocationAutocomplete
                        id="location"
                        value={location}
                        onChange={setLocation}
                        placeholder="např. Brno, Žižkov, Ostrava"
                        variant="down"
                    />
                </div>

                {/* Řidičský průkaz */}
                <div className="driver_license" ref={selectRefLicense}>
                    <label
                        htmlFor="driver_license"
                        className="body_base label-move"
                    >
                        Řidičský průkaz
                    </label>
                    <div className="custom-select-wrapper">
                        <div
                            className={`custom-select-down ${isOpenLicense ? "open" : ""}`}
                            onClick={() => setIsOpenLicense(!isOpenLicense)}
                        >
                            <span className="selected">
                                {LICENSE_OPTIONS.find(
                                    (opt) => opt.value === driverLicense,
                                )?.label || "Nemám"}
                            </span>
                            <span
                                className={`arrow ${isOpenLicense ? "rotate" : ""}`}
                            >
                                ▼
                            </span>
                        </div>
                        {isOpenLicense && (
                            <div className="options-down">
                                {LICENSE_OPTIONS.map((opt) => (
                                    <div
                                        key={opt.value || "none"}
                                        className={`option ${driverLicense === opt.value ? "selected" : ""}`}
                                        onClick={() => {
                                            setDriverLicense(opt.value);
                                            setIsOpenLicense(false);
                                        }}
                                    >
                                        {opt.label}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Email */}
                <div className="email">
                    <label htmlFor="email" className="body_base label-move">
                        Email
                    </label>
                    <InputLogin
                        type="email"
                        name="email"
                        value={email || ""}
                        required
                        extraClass="disabled-input"
                        disabled
                    />
                </div>

                {/* Telefon */}
                <div className="phone">
                    <label htmlFor="phone" className="body_base label-move">
                        Telefon
                    </label>
                    <input
                        type="tel"
                        id="phone1"
                        className="input-login"
                        ref={phoneFormRef}
                        onBlur={handlePhoneBlur}
                    />

                    {/* Ikona upozornění při změně telefonního čísla */}
                    {phoneChanged && currentPhone !== "" && (
                        <FontAwesomeIcon
                            icon={faCircleExclamation}
                            className="input_error"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowPhonePopup(true);
                            }}
                        />
                    )}

                    <div className="checkbox-container">
                        <input
                            type="checkbox"
                            id="visible"
                            className="custom-checkbox"
                            checked={phoneVisible}
                            onChange={() => setPhoneVisible(!phoneVisible)}
                        />
                        <label htmlFor="visible" className="checkbox-text">
                            Viditelné
                        </label>
                    </div>
                </div>

                {/* Submit */}
                <button
                    type="submit"
                    className="form-submit form-edit-profile-submit div8"
                >
                    <p className="strong">Uložit změny</p>
                </button>
            </form>
        </>
    );
};

export default FormEditProfile;
